/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, ReactElement } from 'react'
import { useQuery } from '@tanstack/react-query'
import { parseAsBoolean, parseAsString, useQueryState } from 'nuqs'

import { Label } from '~/components/UI/shadcn/label'
import Dropdown from '~/components/UI/Forms/Dropdown/Dropdown'
import RadioButtons from '~/components/UI/Forms/RadioButtons/RadioButtons'
import Checkboxes from '~/components/UI/Forms/Checkboxes/Checkboxes'
import FormUISlider from '~/components/UI/Forms/Slider/Slider'
import NestedUIPagination from '~/components/UI/General/Pagination/Pagination'
import Options from './components/Options'

import { useMediaQuery } from 'usehooks-ts'
import { cn } from '~/lib/utils'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import ComboBox from '~/components/UI/Forms/ComboBox/ComboBox'

import { getEntryById, getResults } from './data'
import ResultsList from './components/ResultsList'
import { Skeleton } from '~/components/UI/shadcn/skeleton'
import FormUISwitch from '~/components/UI/Forms/Switch/Switch'

type QueryData = { items: any[] }

// export const filterMap: Record<string, string> = {
//   year: 'courseVariants[0].years',
//   duration: 'courseVariants[0].duration'
// }

function getActiveFilters(filters: any[], urlParams: URLSearchParams) {
  return filters.reduce((acc: any, filter: any) => {
    const key = filter.value.queryKey
    const filterValue = urlParams.get(key)
    if (filterValue && filterValue !== '') {
      acc[key] = {
        value: filterValue,
        type: filter.type,
        subType: filter.value.filterType
      }
    }
    return acc
  }, {})
}

function filterItems(items: any[], filters: any[], urlParams: URLSearchParams) {
  if (!Array.isArray(filters) || filters.length === 0) return items
  const activeFilters = getActiveFilters(filters, urlParams)
  // console.log(filters)
  if (Object.keys(activeFilters).length === 0) return items

  return items.filter((item: { [x: string]: any }) =>
    Object.entries(activeFilters).every(
      ([key, filterConfig]: [string, any]) => {
        const {
          value: filterValue,
          type: filterType,
          subType: filterSubType
        } = filterConfig
        const itemValue =
          // item.sys.contentTypeId === 'studyUndergraduateCoursePage' &&
          item.sys.contentTypeId.includes('Course') &&
          ['years', 'duration'].includes(key)
            ? item.courseVariants[0][key]
            : item[key]

        if (filterSubType === 'checkbox') {
          const checkboxValues = filterValue
            .split(',')
            .map((v: string) => v.trim())
          return checkboxValues.some((selectedValue: string) => {
            if (Array.isArray(itemValue)) {
              return itemValue.some(
                arrayItem =>
                  (arrayItem?.entryDescription ||
                    arrayItem?.toString() ||
                    '') === selectedValue
              )
            }
            return (
              (itemValue?.entryDescription || itemValue?.toString() || '') ===
              selectedValue
            )
          })
        }

        if (filterType === 'uiFilterSlider') {
          const sliderValues = filterValue
            .split(',')
            .map((v: string) => parseInt(v.trim()))
            .filter((v: number) => !isNaN(v))
          let numericItemValue
          if (Array.isArray(itemValue)) {
            const numericValues = itemValue
              .map(item => parseInt(item?.entryDescription || '0'))
              .filter(val => !isNaN(val))
            numericItemValue = numericValues.length > 0 ? numericValues[0] : 0
          } else if (
            typeof itemValue === 'object' &&
            itemValue?.entryDescription
          ) {
            numericItemValue = parseInt(itemValue.entryDescription)
          } else {
            numericItemValue = Number(itemValue)
          }
          if (!isNaN(numericItemValue)) {
            if (sliderValues.length === 2) {
              const [min, max] = sliderValues
              return numericItemValue >= min && numericItemValue <= max
            } else if (sliderValues.length === 1) {
              return numericItemValue >= sliderValues[0]
            }
          }
          return true
        }

        if (filterType === 'uiFiltersSwitch') {
          if (filterValue === false || filterValue === 'false') {
            return !item.virtual
          }
          return true
        }

        if (filterSubType === 'radio') {
          if (Array.isArray(itemValue)) {
            return itemValue.some(
              arrayItem =>
                (arrayItem?.entryDescription || arrayItem?.toString() || '') ===
                filterValue
            )
          }
          return (
            (itemValue?.entryDescription || itemValue?.toString() || '') ===
            filterValue
          )
        }

        if (Array.isArray(itemValue)) {
          return itemValue.some(arrayItem =>
            (arrayItem?.entryDescription || arrayItem?.toString() || '')
              .toLowerCase()
              .includes(filterValue.toLowerCase())
          )
        }
        return (itemValue?.entryDescription || itemValue?.toString() || '')
          .toLowerCase()
          .includes(filterValue.toLowerCase())
      }
    )
  )
}

const Filters = ({ content }: any) => {
  const [page, setPage] = useQueryState('page', parseAsString.withDefault('1'))

  const pageSize = Number(content?.pageSize) || 6
  const currentPage = Number(page) || 1

  const filterKeys = content.filters.map((filter: any) => filter.value.queryKey)

  // console.log('filterKeys', filterKeys)

  const filterSetters = filterKeys.reduce((acc: any, key: string) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [, setValue] = useQueryState(key)
    acc[key] = setValue
    return acc
  }, {})

  const urlParams = new URLSearchParams(window.location.search)
  const selectedCount = filterKeys.filter((key: string) => {
    const value = urlParams.get(key)
    return value !== null && value !== ''
  }).length

  // console.log('filterSetters', filterSetters)

  const clearFilters = useCallback(() => {
    filterKeys.forEach((key: string | number) => {
      if (filterSetters[key]) filterSetters[key](null)
    })
    setPage('1')
  }, [filterKeys, filterSetters, setPage])

  // eslint-disable-next-line prefer-const
  let { data: filteredData = [], isLoading } = useQuery<
    QueryData,
    Error,
    any[]
  >({
    queryKey: ['filterData', content?.resultSection?.id, content?.resultItems],
    queryFn: async () => {
      try {
        if (
          Array.isArray(content?.resultItems) &&
          content.resultItems.length > 0
        ) {
          const entries = await Promise.all(
            content.resultItems.map((r: any) => getEntryById(r.sys.id))
          )
          return { items: entries.filter(Boolean) }
        }
        if (content?.resultSection?.id) {
          const result = await getResults(content.resultSection.id)
          if (result && Array.isArray((result as any).items)) {
            return { items: (result as any).items }
          }
          return { items: [] }
        }
        return { items: [] }
      } catch (e) {
        // Optionally log error
        return { items: [] }
      }
    },
    enabled:
      !!content?.resultSection?.id ||
      (Array.isArray(content?.resultItems) && content.resultItems.length > 0),
    select: (data: QueryData) => {
      let items: any[] = []
      if (
        Array.isArray(content?.resultItems) &&
        content.resultItems.length > 0
      ) {
        items = data.items
      } else if (Array.isArray(data?.items) && data.items.length > 0) {
        items = data.items
      }
      const urlParams = new URLSearchParams(window.location.search)
      return filterItems(items, content?.filters, urlParams)
    }
  })

  // if (
  //   filteredData &&
  //   filteredData.length > 0 &&
  //   filteredData[0]?.sys?.contentTypeId === 'pageNewsArticle'
  // ) {
  //   filteredData.sort(
  //     (a, b) => new Date(b?.date).getDate() - new Date(a?.date).getDate()
  //   )
  // }
  const [past] = useQueryState<boolean>('past', parseAsBoolean)
  if (
    filteredData &&
    filteredData.length > 0 &&
    filteredData[0]?.sys?.contentTypeId === 'pageEvent'
  ) {
    const data = filteredData
      // .sort(
      //   (a, b) =>
      //     new Date(b?.date?.from).getTime() - new Date(a?.date?.from).getTime()
      // )
      .filter(item =>
        !past ? new Date(item?.date?.from) >= new Date(Date.now()) : item
      )
    filteredData = data
  }

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  const totalPages = Math.ceil(Number(filteredData?.length) / Number(pageSize))
  const safePage = currentPage > totalPages ? 1 : currentPage

  const isDesktop = useMediaQuery('(width >= 64rem)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isMobile = useMediaQuery('(width <= 36rem)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const renderFilter = (filter: any) => {
    const {
      filterType,
      label,
      note,
      options,
      min,
      max,
      value,
      prefixSuffix,
      numericalLabels,
      queryKey
    } = filter.value

    // console.log('filter', filter)

    const filterComponents: Record<string, ReactElement> = {
      checkbox: (
        <Checkboxes
          data={{ label, required: false, note }}
          items={options}
          disabled={false}
          queryKey={queryKey}
        />
      ),
      radio: (
        <RadioButtons
          disabled={false}
          data={{ label, required: false, note }}
          items={options}
          queryKey={queryKey}
        />
      ),
      dropdown: (
        <Dropdown
          data={{ label, required: false, note }}
          style='Outline'
          items={options}
          queryKey={queryKey}
        />
      ),
      combobox: (
        <ComboBox
          data={{ label, required: false, note }}
          style='Outline'
          items={options}
          queryKey={queryKey}
        />
      ),
      slider: (
        <div className='space-y-l mb-m'>
          {label && <Label className='flex justify-between'>{label}</Label>}
          <div className='mx-auto'>
            <FormUISlider
              disabled={false}
              start={!prefixSuffix && numericalLabels}
              end={prefixSuffix && numericalLabels}
              value={value}
              min={min}
              max={max}
              step={1}
              queryKey={queryKey}
            />
          </div>
          {note && <p className='text-xs'>{note}</p>}
        </div>
      ),
      switch: (
        <FormUISwitch
          label={label}
          disabled={false}
          bgColour={'mid-blue'}
          queryKey={queryKey}
          checked={value}
        />
      )
    }

    if (min !== undefined && max !== undefined && value !== undefined) {
      return filterComponents.slider
    }

    if (filter.type === 'uiFiltersSwitch') {
      return filterComponents.switch
    }

    return filterComponents[filterType] || null
  }
  const Filters = useMemo(() => {
    return content.filters.map((filter: any, index: number) => (
      <div
        key={`${filter.value.options?.id || 'filter'}-${index}`}
        className='pb-m'
      >
        {renderFilter(filter)}
      </div>
    ))
  }, [content.filters])

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(String(newPage))
    },
    [setPage]
  )

  return (
    <div
      className={cn(
        isDesktop
          ? 'grid-cols-[minmax(300px,_400px)_minmax(400px,_1fr)]'
          : 'grid-cols-[60px_1fr]',
        'gap-m md:gap-xl lg:gap-2xl relative grid'
      )}
    >
      <div className='pr-xs pl-3xs sticky top-[120px] h-fit max-h-[80vh] overflow-auto'>
        <Options selectedCount={selectedCount}>
          <div className='divide-grey-m space-y-m divide-y-2 *:last:pb-0'>
            {Filters}

            <div className='flex justify-end'>
              <TertiaryLink
                data={{
                  strongColour: false,
                  externalLink: null,
                  internalLink: {
                    sys: {
                      uri: '/'
                    }
                  },
                  ariaLabel: 'Clear filters',
                  newWindow: true,
                  label: 'Clear filters'
                }}
                colour='red'
                variant={'cancel'}
                onClick={() => clearFilters()}
              />
            </div>
          </div>
        </Options>
      </div>
      <div className='space-y-l @container/results'>
        {/* {!isDesktop && (
          <div className='divide-grey-m flex divide-x-2'>
            <div className='pr-xs-plus'>
              <span className='tag text-grey-d/70'>Contract</span>
              <p className='font-bold'>43 weeks</p>
            </div>
            <div className='px-xs-plus'>
              <span className='tag text-grey-d/70'>Max price</span>
              <p className='font-bold'>£200 pw</p>
            </div>
            {!isMobile && (
              <div className='pl-xs-plus'>
                <span className='tag text-grey-d/70'>Bathroom option</span>
                <p className='font-bold'>En-suite</p>
              </div>
            )}
            {isMobile && (
              <div className='pl-xs-plus p-2xs flex grow justify-end'>
                <span className='tag text-grey-d/70 max-w-[40px] text-right'>
                  + 2 more
                </span>
              </div>
            )}
          </div>
        )} */}
        {/* gap-xs grid grid-cols-1 @xl/results:grid-cols-2 @4xl/results:grid-cols-3 */}
        <div className='space-y-m h-full'>
          <div
            className={cn(
              content?.resultSection?.id === 'pageCaseStudyResearch' ||
                content?.resultSection?.id === 'staffProfile'
                ? 'gap-s flex flex-wrap'
                : ''
            )}
          >
            {(() => {
              if (isLoading) {
                return (
                  <div className='space-y-m flex h-full w-full flex-col'>
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className='space-y-s flex w-full flex-col'>
                        <div className='gap-xs-plus flex w-full flex-row items-center'>
                          <Skeleton className='aspect-square h-[150px]' />
                          <div className='space-y-xs flex grow flex-col'>
                            <Skeleton className='h-m w-3/4' />
                            <Skeleton className='h-xs w-xl' />
                            <Skeleton className='h-xs w-2/3' />
                            <Skeleton className='h-xs w-1/2' />
                          </div>
                        </div>
                        <Skeleton className='h-3xs w-full' />
                      </div>
                    ))}
                  </div>
                )
              }

              if (!filteredData || filteredData.length === 0) {
                return (
                  <p className='text-center'>
                    Your query has returned 0 results
                  </p>
                )
              }

              return (
                <ResultsList
                  items={paginatedData}
                  contentType={
                    content?.resultSection?.id ||
                    filteredData[0]?.sys?.contentTypeId
                  }
                />
              )
            })()}
          </div>

          {!isMobile && (
            <div className='pt-m'>
              <NestedUIPagination
                key={filteredData?.length} // data?.totalCount
                index={safePage}
                total={Number(filteredData?.length)} // data?.totalCount
                size={Number(pageSize)} // data?.pageSize
                onPageChange={handlePageChange}
                className='justify-start'
              />
            </div>
          )}
        </div>
      </div>
      {isMobile && (
        <div className='mt-m'>
          <NestedUIPagination
            key={filteredData?.length}
            index={safePage}
            total={Number(filteredData?.length)} // data?.totalCount
            size={Number(pageSize)} // data?.pageSize
            onPageChange={handlePageChange}
            className='justify-start'
          />
        </div>
      )}
    </div>
  )
}

export default Filters
